/**
 * Hook: Facility selection state and derived values for Cleaning Standards page.
 * Single Responsibility: which facilities are selected, select/all handlers, derived lists.
 */

import { useState, useCallback, useMemo } from 'react';

/**
 * Pure: toggle a facility id in a list (add if absent, remove if present).
 * @param {string[]} currentIds
 * @param {string} facilityId
 * @returns {string[]}
 */
export function toggleFacilitySelection(currentIds, facilityId) {
  if (currentIds.includes(facilityId)) {
    return currentIds.filter((id) => id !== facilityId);
  }
  return [...currentIds, facilityId];
}

/**
 * Pure: select all or none based on current selection count vs total.
 * @param {string[]} currentIds
 * @param {Array<{ id: string }>} facilities
 * @returns {string[]}
 */
export function selectAllFacilities(currentIds, facilities) {
  const allIds = (facilities || []).map((f) => f.id).filter(Boolean);
  const allSelected = allIds.length > 0 && currentIds.length === allIds.length;
  return allSelected ? [] : allIds;
}

export function useFacilitySelection(facilitiesFromList) {
  const [selectedFacilityIds, setSelectedFacilityIds] = useState([]);

  const handleSelectFacility = useCallback((facilityId) => {
    setSelectedFacilityIds((prev) => toggleFacilitySelection(prev, facilityId));
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedFacilityIds((prev) =>
      selectAllFacilities(prev, facilitiesFromList),
    );
  }, [facilitiesFromList]);

  const selectedFacilities = useMemo(
    () =>
      facilitiesFromList.filter((f) => selectedFacilityIds.includes(f.id)),
    [facilitiesFromList, selectedFacilityIds],
  );

  const selectedFacilityNames = useMemo(
    () => selectedFacilities.map((f) => f.name),
    [selectedFacilities],
  );

  const isSingleFacilitySelected = selectedFacilityIds.length === 1;

  return {
    selectedFacilityIds,
    handleSelectFacility,
    handleSelectAll,
    selectedFacilities,
    selectedFacilityNames,
    isSingleFacilitySelected,
  };
}
